import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm } from 'react-hook-form';

import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useCheckMethodsForEmail, useSignIn } from '@/features/auth/queries';
import type { LoginFormValues } from '@/features/auth/schema';
import { loginSchema } from '@/features/auth/schema';
import { setPendingCredential } from '@/features/auth/utils/pending-credential';
import {
  setVerificationSentAt,
  shouldResendVerification,
} from '@/features/auth/utils/verify-email-throttle';
import { useUnAuthStackNavigation } from '@/hooks/use-navigation';
import { sendEmailVerification, signOut } from '@/libs/firebase/auth/auth-helpers';
import { captureException } from '@/libs/logger';
import { useSessionStore, useUiStore } from '@/stores';
import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';

export function useLogin() {
  // const rootNavigation = useRootNavigation();
  const { mutateAsync: checkMethodsForEmail } = useCheckMethodsForEmail();
  // const { mutateAsync: lookupUserAccount } = useLookupUserAccount();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const { mutateAsync: signIn } = useSignIn();
  const navigation = useUnAuthStackNavigation();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const {
    field: { value: email, onChange: onChangeEmail, onBlur: onBlurEmail },
  } = useController({
    name: 'email',
    control,
  });

  const {
    field: { value: password, onChange: onChangePassword, onBlur: onBlurPassword },
  } = useController({
    name: 'password',
    control,
  });

  const handleNext = useCallback(
    async (_data: LoginFormValues) => {
      try {
        showGlobalLoading();
        const methods = await checkMethodsForEmail(_data.email);
        if (methods.length > 0) {
          const userAccount = await signIn({
            email: _data.email,
            password: _data.password,
          });
          if (!userAccount.user.emailVerified) {
            const userEmail = userAccount.user.email || '';
            // PP-330: only re-trigger Firebase once the cooldown has elapsed to
            // avoid `auth/too-many-requests`; otherwise just show the screen.
            if (shouldResendVerification(userEmail, Date.now())) {
              await sendEmailVerification(userAccount.user);
              setVerificationSentAt(userEmail, Date.now());
            }
            navigation.navigate('VerifyEmailLinkSent', { email: userEmail });
            signOut();
          } else {
            // Verified sign-in: stash the credential so the biometric opt-in at
            // onboarding step 4 can persist it — the password only lives in the
            // form until this point. AuthStateEffect drives navigation from here.
            setPendingCredential({
              email: _data.email,
              password: _data.password,
              provider: 'email',
            });
            // Clear any soft-logout lock. On the biometric login screen the Firebase
            // session is still alive, so re-signing in with the same account may not
            // re-fire onAuthStateChanged — clear it here so `withUnAuth` lets the user
            // back in.
            useSessionStore.getState().setSoftLoggedOut(false);
          }
        } else {
          showAlert({
            title: STRINGS.auth.enterEmail.wrongCredentialTitle,
            message: STRINGS.auth.enterEmail.wrongCredentialDescription,
          });
        }
      } catch (error) {
        if ((error as any)?.code === 'auth/too-many-requests') {
          showAlert({
            title: STRINGS.auth.enterEmail.noticeTitle,
            message: STRINGS.auth.login.tooManyRequest,
          });
        }

        captureException(
          {
            error_payload: error,
          },
          { tags: { usk_log_type: 'next_enter_email' } },
        );
      } finally {
        hideGlobalLoading();
      }
    },
    [checkMethodsForEmail, hideGlobalLoading, navigation, showGlobalLoading, signIn],
  );

  const handleFormSubmit = handleSubmit(handleNext);

  return {
    email,
    onChangeEmail,
    onBlurEmail,
    errors,
    isValid,
    isSubmitting,
    handleSubmit: handleFormSubmit,
    handleNext,
    password,
    onChangePassword,
    onBlurPassword,
  };
}
